/**
 * Annotation Custom Element (Web Component) - Lit Version
 */

import { LitElement, html, nothing } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { ref, createRef, type Ref } from 'lit/directives/ref.js';
import type { AppState, Settings, OutputLevel, ThemeMode, BeforeAnnotationCreateHook } from '../core/types';
import { createAnnotationCore, type AnnotationCore } from '../core/controller';
import { resolveTheme } from '../themes/variables';
import { componentStyles } from './styles';
import { calculatePopupPosition } from './popup-position';
import {
  renderCollapsedToolbar,
  renderExpandedToolbar,
  renderMarkers,
  renderHoverTooltip,
  renderHighlight,
  renderSelectionRect,
  renderSettingsPanel,
  icons,
} from './templates';
import { normalizeRect } from '../core/dom/multi-select';
import { t } from '../core/i18n';
import { getCurrentRoute, isAnnotationVisibleOnRoute, getAnnotationRoute } from '../core/annotations/route';
import { createDevtoolsApi, attachDevtoolsApi, detachDevtoolsApi, type DevtoolsApi } from './devtools-api';
import { refindElement } from '../core/dom/element-refinder';
import { setDraggingMode } from '../core/dom/cursor';
import {
  clampToolbarPosition,
  computeDraggedToolbarPosition,
  hasExceededDragThreshold,
  TOOLBAR_VIEWPORT_PADDING,
} from '../core/dom/toolbar-drag';
import { saveToolbarPosition } from '../core/annotations/persistence';
import type { Position } from '../core/types';

/**
 * Annotation Web Component
 *
 * Usage:
 * ```html
 * <agent-ui-annotation theme="auto" output-level="standard"></agent-ui-annotation>
 * ```
 *
 * Events:
 * - annotation:create - Fired when an annotation is created
 * - annotation:update - Fired when an annotation is updated
 * - annotation:delete - Fired when an annotation is deleted
 * - annotation:clear - Fired when all annotations are cleared
 * - annotation:copy - Fired when output is copied
 */
export class AnnotationElement extends LitElement {
  static styles = componentStyles;

  // Static properties (no decorators for lighter bundle)
  // Using 'declare' to avoid class field shadowing Lit's accessors
  // See: https://lit.dev/msg/class-field-shadowing
  static properties = {
    theme: { type: String, reflect: true },
    outputLevel: { type: String, attribute: 'output-level' },
    annotationColor: { type: String, attribute: 'annotation-color' },
    disabled: { type: Boolean },
  };

  // Public properties (from attributes)
  // Use 'declare' so TypeScript knows about them without generating class fields
  declare theme: ThemeMode;
  declare outputLevel: OutputLevel;
  declare annotationColor: string;
  declare disabled: boolean;

  constructor() {
    super();
    // Initialize default values in constructor instead of class fields
    this.theme = 'auto';
    this.outputLevel = 'standard';
    this.annotationColor = '#AF52DE';
    this.disabled = false;
  }

  // Core controller
  private core: AnnotationCore | null = null;
  private unsubscribe: (() => void) | null = null;
  private beforeCreateHook: BeforeAnnotationCreateHook | null = null;

  // Internal state
  private appState: AppState | null = null;
  private popupComment: string = '';
  private popupShaking: boolean = false;
  private mouseX: number = 0;
  private mouseY: number = 0;
  private hoveredMarkerId: string | null = null;

  // Animation tracking
  private toolbarShownOnce: boolean = false;
  private settingsPanelAnimated: boolean = false;
  private annotationsPanelAnimated: boolean = false;
  private animatedMarkerTooltipId: string | null = null;
  private lastRenderedSettings: string | null = null;
  private showCountSummary: boolean = false;

  // Bound handlers for cleanup
  private boundHandleResize = () => this.handleWindowResize();
  private boundHandleMouseMove = (e: MouseEvent) => this.handleMouseMove(e);
  private boundHandleDocumentClick = (e: Event) => this.handleDocumentClick(e);
  private boundHandleScroll = () => this.handleScroll();
  private boundHandleToolbarPointerMove = (e: PointerEvent) => this.handleToolbarPointerMove(e);
  private boundHandleToolbarPointerUp = (e: PointerEvent) => this.handleToolbarPointerUp(e);
  private scrollRafPending = false;

  // Toolbar drag session (kept off the store so mid-drag hovers do not snap the toolbar)
  private toolbarDrag: {
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    width: number;
    height: number;
    didDrag: boolean;
  } | null = null;
  private dragPosition: Position | null = null;
  private suppressToolbarClick = false;

  // Textarea ref for autofocus
  private textareaRef: Ref<HTMLTextAreaElement> = createRef();
  private popupPosition: { left: number; top: number } | null = null;
  private currentRoute: string = getCurrentRoute();
  private routeListenerCleanup: (() => void) | null = null;
  private devtoolsApi: DevtoolsApi | null = null;

  connectedCallback() {
    super.connectedCallback();

    // Expose instance for devtools automation
    this.devtoolsApi = createDevtoolsApi(this);
    attachDevtoolsApi(this, this.devtoolsApi);

    // Initialize core
    this.core = createAnnotationCore({
      settings: this.getSettingsFromAttributes(),
      loadPersisted: true,
      onBeforeAnnotationCreate: this.beforeCreateHook ?? undefined,
      onAnnotationCreate: (annotation) => this.dispatchAnnotationEvent('annotation:create', { annotation }),
      onAnnotationUpdate: (annotation) => this.dispatchAnnotationEvent('annotation:update', { annotation }),
      onAnnotationDelete: (id) => this.dispatchAnnotationEvent('annotation:delete', { id }),
      onAnnotationsClear: (annotations) => this.dispatchAnnotationEvent('annotation:clear', { annotations }),
      onCopy: (content, level) => this.dispatchAnnotationEvent('annotation:copy', { content, level }),
    });

    this.routeListenerCleanup = this.bindRouteListeners();

    // Subscribe to state changes
    this.unsubscribe = this.core.subscribe((state) => {
      this.appState = state;
      // Reset popup comment when popup closes or opens for a different annotation
      if (!state.popupVisible) {
        this.popupComment = '';
      } else if (state.popupAnnotationId) {
        const annotation = state.annotations.get(state.popupAnnotationId);
        this.popupComment = annotation?.comment || '';
      }
      this.requestUpdate();
    });

    // Set up event listeners
    document.addEventListener('mousemove', this.boundHandleMouseMove);
    document.addEventListener('click', this.boundHandleDocumentClick);
    window.addEventListener('resize', this.boundHandleResize);
    document.addEventListener('scroll', this.boundHandleScroll, { capture: true, passive: true });

    // Initial state
    this.appState = this.core.store.getState();
    this.updateThemeAttribute();

    // Re-find DOM elements for persisted annotations once the DOM is settled.
    // Use rAF to allow frameworks (React, Vue, etc.) to finish hydration first.
    requestAnimationFrame(() => this.refindAnnotationElements());
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    detachDevtoolsApi(this, this.devtoolsApi);
    this.devtoolsApi = null;

    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }

    if (this.routeListenerCleanup) {
      this.routeListenerCleanup();
      this.routeListenerCleanup = null;
    }

    window.removeEventListener('resize', this.boundHandleResize);
    document.removeEventListener('scroll', this.boundHandleScroll, true);
    document.removeEventListener('mousemove', this.boundHandleMouseMove);
    document.removeEventListener('click', this.boundHandleDocumentClick);
    this.teardownToolbarDrag();

    this.teardownPopupObserver();

    if (this.core) {
      this.core.destroy();
      this.core = null;
    }
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);

    // Handle attribute changes
    if (changedProperties.has('theme') && this.core) {
      this.core.updateSettings({ theme: this.theme });
      this.updateThemeAttribute();
    }

    if (changedProperties.has('outputLevel') && this.core) {
      this.core.updateSettings({ outputLevel: this.outputLevel });
    }

    if (changedProperties.has('annotationColor') && this.core) {
      this.core.updateSettings({ annotationColor: this.annotationColor });
    }

    if (changedProperties.has('disabled') && this.core && this.disabled) {
      this.core.deactivate();
    }

    // Focus textarea when popup opens
    if (this.appState?.popupVisible && this.textareaRef.value) {
      this.textareaRef.value.focus();
    }

    this.syncPopupPosition();
  }

  /**
   * Public API
   */

  /**
   * Set the hook called before creating annotations.
   * This must be called before connectedCallback runs (i.e., before the element is added to the DOM)
   * OR the component must be re-initialized after setting.
   *
   * For framework adapters, call this immediately after element creation.
   */
  setBeforeCreateHook(hook: BeforeAnnotationCreateHook | null) {
    this.beforeCreateHook = hook;

    // If core already exists, we need to recreate it to apply the hook
    // This handles the case where the hook is set after the element is connected
    if (this.core) {
      // Store current state
      const wasActive = this.core.isActive();
      const currentMode = this.core.getMode();

      // Destroy old core
      if (this.unsubscribe) {
        this.unsubscribe();
        this.unsubscribe = null;
      }
      this.core.destroy();

      // Create new core with hook
      this.core = createAnnotationCore({
        settings: this.getSettingsFromAttributes(),
        loadPersisted: true,
        onBeforeAnnotationCreate: hook ?? undefined,
        onAnnotationCreate: (annotation) => this.dispatchAnnotationEvent('annotation:create', { annotation }),
        onAnnotationUpdate: (annotation) => this.dispatchAnnotationEvent('annotation:update', { annotation }),
        onAnnotationDelete: (id) => this.dispatchAnnotationEvent('annotation:delete', { id }),
        onAnnotationsClear: (annotations) => this.dispatchAnnotationEvent('annotation:clear', { annotations }),
        onCopy: (content, level) => this.dispatchAnnotationEvent('annotation:copy', { content, level }),
      });

      // Subscribe to state changes
      this.unsubscribe = this.core.subscribe((state) => {
        this.appState = state;
        if (!state.popupVisible) {
          this.popupComment = '';
        } else if (state.popupAnnotationId) {
          const annotation = state.annotations.get(state.popupAnnotationId);
          this.popupComment = annotation?.comment || '';
        }
        this.requestUpdate();
      });

      this.appState = this.core.store.getState();

      // Restore active state if it was active
      if (wasActive && currentMode !== 'disabled') {
        this.core.activate(currentMode);
      }
    }
  }

  activate() {
    this.core?.activate();
  }

  deactivate() {
    this.core?.deactivate();
  }

  toggle() {
    this.core?.toggle();
  }

  async copyOutput(level?: OutputLevel): Promise<boolean> {
    return this.core?.copyOutput(level) ?? false;
  }

  getOutput(level?: OutputLevel): string {
    return this.core?.getOutput(level) ?? '';
  }

  clearAll() {
    this.core?.annotations.clearAllAnnotations();
  }

  /**
   * Private methods
   */

  private getSettingsFromAttributes(): Partial<Settings> {
    const settings: Partial<Settings> = {};

    if (this.theme === 'light' || this.theme === 'dark' || this.theme === 'auto') {
      settings.theme = this.theme;
    }

    if (this.outputLevel === 'compact' || this.outputLevel === 'standard' || this.outputLevel === 'detailed' || this.outputLevel === 'forensic') {
      settings.outputLevel = this.outputLevel;
    }

    if (this.annotationColor) {
      settings.annotationColor = this.annotationColor;
    }

    return settings;
  }

  private updateThemeAttribute() {
    if (!this.core) return;

    const theme = this.core.getSettings().theme;
    const resolved = resolveTheme(theme);

    this.setAttribute('data-theme', theme);
    if (theme === 'auto') {
      this.setAttribute('data-resolved-theme', resolved);
    }
  }

  private handleWindowResize() {
    if (!this.core) return;

    const state = this.core.store.getState();

    // Recompute marker positions from live element references
    const newAnnotations = new Map<string, typeof state.annotations extends Map<string, infer V> ? V : never>();
    for (const [id, annotation] of state.annotations) {
      if (!annotation.element || !annotation.element.isConnected) {
        // Keep annotation as-is if element is gone
        newAnnotations.set(id, annotation);
        continue;
      }

      const rect = annotation.element.getBoundingClientRect();

      // Use stored offset percentage to maintain relative position within element
      const offsetXPercent = annotation.offsetX;
      const offsetYPercent = annotation.offsetY;

      const newClickX = rect.left + (rect.width * offsetXPercent);
      const newClickY = rect.top + (rect.height * offsetYPercent) + window.scrollY;

      // Always create new annotation object to ensure state change is detected
      newAnnotations.set(id, { ...annotation, clickX: newClickX, clickY: newClickY });
    }

    // Always update state with fresh annotations and scroll position
    this.core.store.setState({
      annotations: newAnnotations,
      scrollY: window.scrollY,
    });

    this.clampAndPersistToolbarPosition();
  }

  /**
   * Attempt to re-find DOM elements for persisted annotations that
   * lost their element reference (e.g., after page reload).
   * Uses the stored selectorPath, ID, classes, and text content.
   * Retries up to MAX_RETRIES times for dynamically loaded content.
   */
  private refindAnnotationElements(retryCount: number = 0) {
    if (!this.core) return;

    const state = this.core.store.getState();
    const updatedAnnotations = new Map(state.annotations);
    let changed = false;
    let allFound = true;

    for (const [id, annotation] of updatedAnnotations) {
      // Only try re-finding if the element is missing or disconnected
      if (annotation.element && annotation.element.isConnected) continue;

      const element = refindElement(annotation.elementInfo);
      if (element) {
        updatedAnnotations.set(id, { ...annotation, element });
        changed = true;
      } else {
        allFound = false;
      }
    }

    if (changed) {
      this.core.store.setState({ annotations: updatedAnnotations });
    }

    // Retry for dynamically loaded content (e.g., async lists, lazy components)
    const MAX_RETRIES = 3;
    if (!allFound && retryCount < MAX_RETRIES) {
      setTimeout(() => this.refindAnnotationElements(retryCount + 1), 100 * (retryCount + 1));
    }
  }

  private handleDocumentClick(event: Event) {
    if (!this.core) return;

    const state = this.core.store.getState();
    if (!state.settingsPanelVisible && !this.showCountSummary) return;

    const path = event.composedPath();
    const clickedInside = path.some((el) => el === this);

    if (!clickedInside) {
      this.showCountSummary = false;
      if (state.settingsPanelVisible) {
        this.core.store.setState({ settingsPanelVisible: false });
      } else {
        this.requestUpdate();
      }
    }
  }

  /**
   * Keep markers tracking their elements on ANY scroll event.
   * Uses capture-phase listener on document to catch nested scroll
   * containers (not just the main page scroll).
   * This listener runs at all times (not just when activated) so that
   * dots-mode markers also move correctly when the toolbar is closed.
   */
  private handleScroll() {
    if (!this.core) return;
    // Always update the main scrollY state
    this.core.store.setState({ scrollY: window.scrollY });
    // Batch re-renders via requestAnimationFrame to avoid excessive updates
    // on pages with frequent scroll activity (e.g. nested scroll containers).
    if (!this.scrollRafPending) {
      this.scrollRafPending = true;
      requestAnimationFrame(() => {
        this.scrollRafPending = false;
        this.requestUpdate();
      });
    }
  }

  private bindRouteListeners(): () => void {
    const routeEventName = 'agent-ui-annotation:route-change';
    const handleRouteChange = () => {
      const nextRoute = getCurrentRoute();
      if (nextRoute !== this.currentRoute) {
        this.currentRoute = nextRoute;
        this.requestUpdate();
        // Re-find elements for annotations that may now be on the visible route
        requestAnimationFrame(() => this.refindAnnotationElements());
      }
    };

    const w = window as any;
    if (!w.__agentUiAnnotationHistoryPatched) {
      const dispatch = () => window.dispatchEvent(new Event(routeEventName));

      // Store originals so they can be restored on disconnect
      w.__agentUiAnnotationOriginalPushState = history.pushState;
      w.__agentUiAnnotationOriginalReplaceState = history.replaceState;

      const wrap = <T extends (...args: any[]) => any>(fn: T): T => {
        return function (this: History, ...args: Parameters<T>): ReturnType<T> {
          const result = fn.apply(this, args);
          dispatch();
          return result;
        } as T;
      };

      history.pushState = wrap(history.pushState.bind(history));
      history.replaceState = wrap(history.replaceState.bind(history));
      w.__agentUiAnnotationHistoryPatched = true;
      w.__agentUiAnnotationHistoryPatchRefCount = 1;
    } else {
      // Track how many instances are using the patch
      w.__agentUiAnnotationHistoryPatchRefCount = (w.__agentUiAnnotationHistoryPatchRefCount || 0) + 1;
    }

    window.addEventListener('popstate', handleRouteChange);
    window.addEventListener('hashchange', handleRouteChange);
    window.addEventListener(routeEventName, handleRouteChange as EventListener);

    // Set initial route
    this.currentRoute = getCurrentRoute();

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      window.removeEventListener('hashchange', handleRouteChange);
      window.removeEventListener(routeEventName, handleRouteChange as EventListener);

      // Restore original history methods when last instance disconnects
      const w2 = window as any;
      if (w2.__agentUiAnnotationHistoryPatched) {
        w2.__agentUiAnnotationHistoryPatchRefCount = (w2.__agentUiAnnotationHistoryPatchRefCount || 1) - 1;
        if (w2.__agentUiAnnotationHistoryPatchRefCount <= 0) {
          if (w2.__agentUiAnnotationOriginalPushState) {
            history.pushState = w2.__agentUiAnnotationOriginalPushState;
          }
          if (w2.__agentUiAnnotationOriginalReplaceState) {
            history.replaceState = w2.__agentUiAnnotationOriginalReplaceState;
          }
          delete w2.__agentUiAnnotationHistoryPatched;
          delete w2.__agentUiAnnotationOriginalPushState;
          delete w2.__agentUiAnnotationOriginalReplaceState;
          delete w2.__agentUiAnnotationHistoryPatchRefCount;
        }
      }
    };
  }

  private handleMouseMove(event: MouseEvent) {
    this.mouseX = event.clientX;
    this.mouseY = event.clientY;
  }

  private closeAnnotationsPanel(requestUpdate: boolean = true) {
    if (!this.showCountSummary) return;
    this.showCountSummary = false;
    if (requestUpdate) {
      this.requestUpdate();
    }
  }

  private toggleMarkerVisibility() {
    if (!this.core) return;

    const state = this.core.store.getState();
    const next = state.markerVisibility === 'full'
      ? 'dots'
      : state.markerVisibility === 'dots'
        ? 'hidden'
        : 'full';
    this.core.store.setState({ markerVisibility: next });
  }

  private toggleTheme() {
    if (!this.core) return;

    const currentTheme = this.core.getSettings().theme;
    const resolved = resolveTheme(currentTheme);
    const newTheme = resolved === 'dark' ? 'light' : 'dark';
    this.core.updateSettings({ theme: newTheme });
    this.updateThemeAttribute();
  }

  private toggleSettingsPanel() {
    if (!this.core) return;

    const currentState = this.core.store.getState();
    this.closeAnnotationsPanel(false);
    this.core.store.setState({ settingsPanelVisible: !currentState.settingsPanelVisible });
  }

  private toggleAnnotationsPanel() {
    if (!this.core) return;

    this.showCountSummary = !this.showCountSummary;
    if (this.showCountSummary) {
      this.core.store.setState({ settingsPanelVisible: false });
    }
    this.requestUpdate();
  }

  private navigateToRoute(target: HTMLElement, event: Event) {
    event.preventDefault();
    const link = target.closest('[data-route-href]') as HTMLElement | null;
    const href = link?.getAttribute('data-route-href');
    if (!href) return;

    try {
      const url = new URL(href);
      history.pushState({}, '', url.pathname + url.search + url.hash);
    } catch {
      history.pushState({}, '', href);
    }
    window.dispatchEvent(new Event('agent-ui-annotation:route-change'));
    this.closeAnnotationsPanel(false);
  }

  private openAnnotationPopup(annotationId: string) {
    if (!this.core) return;

    const annotation = this.core.store.getState().annotations.get(annotationId);
    const element = annotation?.element;

    if (element && element.isConnected) {
      const rect = element.getBoundingClientRect();
      const isInViewport = rect.top >= 0 && rect.bottom <= window.innerHeight;

      if (isInViewport) {
        // Element already visible, show popup at its position
        this.core.showPopup(annotationId);
        return;
      }

      // Scroll into view, then show popup at the element's new position
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const onScrollEnd = () => {
        if (!this.core) return;
        const updatedRect = element.getBoundingClientRect();
        const x = updatedRect.left + updatedRect.width * (annotation.offsetX ?? 0.5);
        const y = updatedRect.top + updatedRect.height * (annotation.offsetY ?? 0.5);
        this.core.showPopup(annotationId, { x, y });
      };
      // scrollIntoView with smooth has no callback; use a timeout as fallback
      setTimeout(onScrollEnd, 400);
      return;
    }

    // No live element, fall back to stored coordinates
    this.core.showPopup(annotationId);
  }

  private handleAction(action: string, target: HTMLElement, event: Event) {
    if (!this.core) return;

    // Close the annotations panel only when the user explicitly interacts with
    // a toolbar-level action (not popup actions, which overlay the panel).
    const isToolbarAction = action === 'toggle' || action === 'close' || action === 'freeze'
      || action === 'toggle-markers' || action === 'copy' || action === 'clear'
      || action === 'theme' || action === 'settings' || action === 'navigate-route';
    if (isToolbarAction) {
      this.closeAnnotationsPanel();
    }

    switch (action) {
      case 'toggle':
        this.core.toggle();
        return;

      case 'close':
        this.core.deactivate();
        return;

      case 'freeze':
        this.core.freeze.toggle();
        return;

      case 'toggle-markers':
        this.toggleMarkerVisibility();
        return;

      case 'copy':
        this.core.copyOutput();
        return;

      case 'clear':
        this.core.annotations.clearAllAnnotations();
        return;

      case 'theme':
        this.toggleTheme();
        return;

      case 'settings':
        this.toggleSettingsPanel();
        return;

      case 'annotations':
        this.toggleAnnotationsPanel();
        return;

      case 'navigate-route':
        this.navigateToRoute(target, event);
        return;

      case 'popup-close':
      case 'popup-cancel':
        this.core.hidePopup();
        return;

      case 'popup-submit':
        this.handlePopupSubmit();
        return;

      case 'popup-delete':
        this.handlePopupDelete();
        return;
    }
  }

  private handleClick(event: Event) {
    if (!this.core) return;

    if (this.suppressToolbarClick) {
      event.preventDefault();
      event.stopPropagation();
      this.suppressToolbarClick = false;
      return;
    }

    const target = event.target as HTMLElement;
    const action = target.closest('[data-action]')?.getAttribute('data-action');
    const annotationId = target.closest('[data-annotation-id]')?.getAttribute('data-annotation-id');

    if (action) {
      this.handleAction(action, target, event);
    }

    // Handle marker click
    if (annotationId && !action) {
      this.openAnnotationPopup(annotationId);
    }

    // Handle settings panel changes
    const settingElement = target.closest('[data-setting]') as HTMLElement;
    if (settingElement) {
      this.handleSettingChange(settingElement);
    }
  }

  private handleSettingChange(settingElement: HTMLElement) {
    if (!this.core) return;

    const setting = settingElement.getAttribute('data-setting');
    const value = settingElement.getAttribute('data-value');

    switch (setting) {
      case 'outputLevel': {
        const select = settingElement as HTMLSelectElement;
        this.core.updateSettings({ outputLevel: select.value as OutputLevel });
        break;
      }

      case 'annotationColor':
        if (value) {
          this.core.updateSettings({ annotationColor: value });
        }
        break;

      case 'blockInteractions':
        this.core.updateSettings({ blockInteractions: value === 'true' });
        break;

      case 'showTooltips':
        this.core.updateSettings({ showTooltips: value === 'true' });
        break;

      case 'autoClearAfterCopy':
        this.core.updateSettings({ autoClearAfterCopy: value === 'true' });
        break;
    }
  }

  private handleMouseOver(event: Event) {
    const target = event.target as HTMLElement;

    // Marker hover
    const marker = target.closest('[data-annotation-id]');
    if (marker) {
      const id = marker.getAttribute('data-annotation-id');
      if (id !== this.hoveredMarkerId) {
        this.hoveredMarkerId = id;
        this.requestUpdate();
      }
      return;
    }

    // annotation count panel is click-only
  }

  private handleMouseOut(event: Event) {
    const target = event.target as HTMLElement;
    const relatedTarget = (event as MouseEvent).relatedTarget as HTMLElement | null;

    // Marker hover
    if (target.closest('[data-annotation-id]')) {
      if (!relatedTarget?.closest(`[data-annotation-id="${this.hoveredMarkerId}"]`)) {
        this.hoveredMarkerId = null;
        this.requestUpdate();
      }
      return;
    }

    // annotation count panel is click-only
  }

  private handlePopupKeyDown(event: KeyboardEvent) {
    // Submit on Enter (unless shift held for newline or IME is composing)
    // event.isComposing is true when Enter is pressed to confirm IME character selection
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      this.handlePopupSubmit();
    }

    // Cancel on Escape
    if (event.key === 'Escape') {
      event.preventDefault();
      this.core?.hidePopup();
    }
  }

  private handlePopupInput(event: Event) {
    const textarea = event.target as HTMLTextAreaElement;
    this.popupComment = textarea.value;
  }

  private async handlePopupSubmit() {
    if (!this.core || !this.appState) return;

    const state = this.appState;
    const comment = this.popupComment.trim();

    if (state.popupAnnotationId) {
      // Update existing annotation
      this.core.annotations.updateAnnotation(state.popupAnnotationId, { comment });
    } else if (state.multiSelectElements.length > 1) {
      // Multi-select: create annotations for all selected elements
      for (let i = 0; i < state.multiSelectElements.length; i++) {
        const element = state.multiSelectElements[i];
        const elementInfo = state.multiSelectInfos[i];

        const rect = element.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        const clickX = centerX;
        const clickY = centerY + window.scrollY;

        // For multi-select, marker is centered so offset is 0.5 (50%)
        const offsetX = 0.5;
        const offsetY = 0.5;

        // Note: addAnnotation may return null if cancelled by hook
        await this.core.annotations.addAnnotation(element, comment, {
          clickX,
          clickY,
          offsetX,
          offsetY,
          isMultiSelect: true,
          elementInfo,
        });
      }
    } else if (state.hoveredElement && state.popupElementInfo) {
      const clickX = state.popupClickX;
      const clickY = state.popupClickY + window.scrollY;

      // Calculate offset as percentage (0-1) from element's top-left corner
      const rect = state.hoveredElement.getBoundingClientRect();
      const offsetX = (state.popupClickX - rect.left) / rect.width;
      const offsetY = (state.popupClickY - rect.top) / rect.height;

      // Note: addAnnotation may return null if cancelled by hook
      await this.core.annotations.addAnnotation(state.hoveredElement, comment, {
        clickX,
        clickY,
        offsetX,
        offsetY,
        elementInfo: state.popupElementInfo,
      });
    }

    this.core.hidePopup();
  }

  private handlePopupDelete() {
    if (!this.core || !this.appState) return;

    if (this.appState.popupAnnotationId) {
      this.core.annotations.deleteAnnotation(this.appState.popupAnnotationId);
      this.core.hidePopup();
    }
  }

  private dispatchAnnotationEvent(name: string, detail: unknown) {
    this.dispatchEvent(
      new CustomEvent(name, {
        detail,
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Generate route-grouped annotation count summary HTML
   */
  private generateCountSummary(annotations: import('../core/types').Annotation[], skipAnimation: boolean = false): string {
    if (annotations.length === 0) return '';

    const routeGroups = new Map<string, import('../core/types').Annotation[]>();
    for (const annotation of annotations) {
      const route = getAnnotationRoute(annotation) || this.currentRoute;
      const list = routeGroups.get(route) || [];
      list.push(annotation);
      routeGroups.set(route, list);
    }

    let html = `<div class="settings-panel annotation-list-panel${skipAnimation ? ' no-animate' : ''}" data-annotation-list-panel><div class="settings-title">${this.escapeHtmlStr(t('toolbar.annotations'))}</div>`;

    if (routeGroups.size === 1) {
      const [, singleRouteAnnotations] = Array.from(routeGroups.entries())[0];
      html += '<div class="annotations-preview-list">';
      for (const annotation of singleRouteAnnotations.sort((a, b) => a.number - b.number)) {
        const commentPreview = annotation.comment.trim() || t('marker.noComment');
        html += `<div class="annotation-preview-item" data-annotation-id="${annotation.id}">`;
        html += `<span class="annotation-preview-marker marker-badge">${annotation.number}</span>`;
        html += `<span class="annotation-preview-target">${this.escapeHtmlStr(annotation.elementInfo.humanReadable)}</span>`;
        html += `<span class="annotation-preview-comment">${this.escapeHtmlStr(commentPreview)}</span>`;
        html += '</div>';
      }
      html += '</div>';
      html += '</div>';
      return html;
    }

    for (const [route, routeAnnotations] of routeGroups) {
      let displayPath: string;
      try {
        displayPath = new URL(route).pathname;
      } catch {
        displayPath = route;
      }

      html += '<details class="annotations-route" open>';
      html += '<summary>';
      html += `<span class="summary-path">${this.escapeHtmlStr(displayPath)}</span>`;
      html += `<span class="summary-count">${routeAnnotations.length}</span>`;
      html += '</summary>';
      html += '<div class="annotations-preview-list">';

      for (const annotation of routeAnnotations.sort((a, b) => a.number - b.number)) {
        const commentPreview = annotation.comment.trim() || t('marker.noComment');
        html += `<div class="annotation-preview-item" data-annotation-id="${annotation.id}">`;
        html += `<span class="annotation-preview-marker marker-badge">${annotation.number}</span>`;
        html += `<span class="annotation-preview-target">${this.escapeHtmlStr(annotation.elementInfo.humanReadable)}</span>`;
        html += `<span class="annotation-preview-comment">${this.escapeHtmlStr(commentPreview)}</span>`;
        html += '</div>';
      }

      html += '</div></details>';
    }
    html += '</div>';

    return html;
  }

  private escapeHtmlStr(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private buildToolbarAndPanelHtml(
    state: AppState,
    settings: Settings,
    annotations: import('../core/types').Annotation[],
    totalAnnotationCount: number,
    resolvedTheme: string
  ): { toolbarHtml: string; annotationsPanelHtml: string } {
    if (!state.toolbarExpanded) {
      this.toolbarShownOnce = false;
      this.settingsPanelAnimated = false;
      this.annotationsPanelAnimated = false;
      return {
        toolbarHtml: renderCollapsedToolbar(totalAnnotationCount),
        annotationsPanelHtml: '',
      };
    }

    const showEntranceAnimation = !this.toolbarShownOnce;
    if (showEntranceAnimation) {
      this.toolbarShownOnce = true;
    }

    let settingsPanelHtml = '';
    if (state.settingsPanelVisible) {
      const skipSettingsAnimation = this.settingsPanelAnimated;
      settingsPanelHtml = renderSettingsPanel({ settings, skipAnimation: skipSettingsAnimation });
      this.settingsPanelAnimated = true;
    } else {
      this.settingsPanelAnimated = false;
    }

    let annotationsPanelHtml = '';
    if (this.showCountSummary && totalAnnotationCount > 0) {
      const skipAnnotationsAnimation = this.annotationsPanelAnimated;
      annotationsPanelHtml = this.generateCountSummary(annotations, skipAnnotationsAnimation);
      this.annotationsPanelAnimated = true;
    } else {
      this.annotationsPanelAnimated = false;
    }

    const toolbarHtml = renderExpandedToolbar({
      annotationCount: totalAnnotationCount,
      isFrozen: state.isFrozen,
      markerVisibility: state.markerVisibility,
      isDarkMode: resolvedTheme === 'dark',
      showCopiedFeedback: state.showCopiedFeedback,
      showClearedFeedback: state.showClearedFeedback,
      showEntranceAnimation,
      settingsPanelHtml,
      annotationsPanelHtml,
    });

    return { toolbarHtml, annotationsPanelHtml: '' };
  }

  /**
   * Render popup using Lit's html template for proper IME/input handling
   */
  private renderPopupTemplate(state: AppState) {
    if (!state.popupVisible) return nothing;

    const existingAnnotation = state.popupAnnotationId ? state.annotations.get(state.popupAnnotationId) : null;
    const elementInfo = existingAnnotation?.elementInfo || state.popupElementInfo;
    const isMultiSelect = state.multiSelectInfos.length > 1;
    const isEditing = !!existingAnnotation;

    if (!elementInfo && !existingAnnotation) return nothing;

    const info = elementInfo!;
    const clickX = state.popupClickX || existingAnnotation?.clickX || 0;
    const clickY = state.popupClickY || (existingAnnotation ? existingAnnotation.clickY - window.scrollY : 0);

    const position = this.popupPosition ?? calculatePopupPosition(clickX, clickY);

    // Build header content
    const headerContent = isMultiSelect
      ? html`
          <div class="popup-multiselect-header">
            <div class="popup-element">${t('popup.elementsSelected', { count: state.multiSelectInfos.length })}</div>
            <ul class="popup-element-list">
              ${state.multiSelectInfos.slice(0, 5).map(i => html`<li>${i.humanReadable}</li>`)}
              ${state.multiSelectInfos.length > 5 ? html`<li>${t('popup.andMore', { count: state.multiSelectInfos.length - 5 })}</li>` : nothing}
            </ul>
          </div>
        `
      : html`
          <div>
            <div class="popup-element">${info.humanReadable}</div>
            <div class="popup-path">${info.selectorPath}</div>
            ${info.componentPath ? html`<div class="popup-component">${info.componentPath}</div>` : nothing}
          </div>
        `;

    return html`
      <div
        class="popup-popover ${this.popupShaking ? 'shake' : ''}"
        style="left: ${position.left}px; top: ${position.top}px;"
        data-annotation-popup
      >
        <div class="popup-header">
          ${headerContent}
          <button class="popup-close" data-action="popup-close" title="${t('popup.close')}">
            ${unsafeHTML(icons.x)}
          </button>
        </div>

        <div class="popup-body">
          <textarea
            ${ref(this.textareaRef)}
            class="popup-textarea"
            placeholder="${isMultiSelect ? t('popup.addFeedbackMulti') : t('popup.addFeedback')}"
            .value=${this.popupComment}
            @input=${this.handlePopupInput}
            @keydown=${this.handlePopupKeyDown}
          ></textarea>
        </div>

        <div class="popup-footer">
          ${isEditing ? html`
            <button class="popup-btn danger" data-action="popup-delete">
              ${t('popup.delete')}
            </button>
          ` : nothing}
          <button class="popup-btn" data-action="popup-cancel">
            ${t('popup.cancel')}
          </button>
          <button class="popup-btn primary" data-action="popup-submit">
            ${isEditing ? t('popup.save') : isMultiSelect ? t('popup.addAnnotations', { count: state.multiSelectInfos.length }) : t('popup.addAnnotation')}
          </button>
        </div>
      </div>
    `;
  }

  render() {
    if (!this.appState || !this.core) {
      return nothing;
    }

    const state = this.appState;
    const settings = state.settings;
    const annotations = Array.from(state.annotations.values()).sort((a, b) => a.number - b.number);
    const visibleAnnotations = annotations.filter((annotation) =>
      isAnnotationVisibleOnRoute(annotation, this.currentRoute)
    );
    const totalAnnotationCount = annotations.length;
    const nextAnnotationNumber = annotations.length > 0
      ? Math.max(...annotations.map((annotation) => annotation.number)) + 1
      : 1;
    const resolvedTheme = resolveTheme(settings.theme);

    // Track animations for settings panel
    if (state.settingsPanelVisible) {
      const currentSettingsKey = JSON.stringify({
        settings,
        settingsPanelVisible: state.settingsPanelVisible,
        annotationCount: totalAnnotationCount,
        isFrozen: state.isFrozen,
        markerVisibility: state.markerVisibility,
        theme: resolvedTheme,
      });

      if (this.lastRenderedSettings !== currentSettingsKey) {
        this.lastRenderedSettings = currentSettingsKey;
      }
    } else {
      this.lastRenderedSettings = null;
    }

    const { toolbarHtml, annotationsPanelHtml } = this.buildToolbarAndPanelHtml(
      state,
      settings,
      annotations,
      totalAnnotationCount,
      resolvedTheme
    );

    // Markers HTML
    // Show markers when:
    //  - toolbar expanded + visibility is 'full' or 'dots'
    //  - toolbar collapsed + visibility is 'dots' (persistent dot indicators)
    const showMarkers = state.markerVisibility !== 'hidden'
      && (state.toolbarExpanded || state.markerVisibility === 'dots');
    let markersHtml = '';
    if (showMarkers) {
      let pendingMarker = null;
      let pendingMarkers: Array<{ x: number; y: number }> = [];

      if (state.popupVisible && !state.popupAnnotationId) {
        if (state.multiSelectElements.length > 1) {
          pendingMarkers = state.multiSelectElements.map((el) => {
            const rect = el.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;

            return {
              x: centerX,
              y: centerY + window.scrollY,
            };
          });
        } else if (state.pendingMarkerX !== 0) {
          pendingMarker = {
            x: state.pendingMarkerX,
            y: state.pendingMarkerY,
          };
        }
      }

      const skipTooltipAnimation = this.hoveredMarkerId !== null && this.hoveredMarkerId === this.animatedMarkerTooltipId;
      if (this.hoveredMarkerId !== null) {
        this.animatedMarkerTooltipId = this.hoveredMarkerId;
      } else {
        this.animatedMarkerTooltipId = null;
      }

      markersHtml = renderMarkers({
        annotations: visibleAnnotations,
        hoveredMarkerId: this.hoveredMarkerId,
        exitingMarkers: state.exitingMarkers,
        animatingMarkers: state.animatingMarkers,
        scrollY: state.scrollY,
        accentColor: settings.annotationColor,
        markerVisibility: state.markerVisibility,
        pendingMarker,
        pendingMarkers,
        nextNumber: nextAnnotationNumber,
        skipTooltipAnimation,
      });
    }

    // Hover tooltip HTML (suppress during Cmd/Ctrl passthrough)
    let tooltipHtml = '';
    let highlightHtml = '';
    if (state.toolbarExpanded && !state.popupVisible && !state.passthroughActive && state.hoveredElementInfo && settings.showTooltips) {
      tooltipHtml = renderHoverTooltip({
        elementInfo: state.hoveredElementInfo,
        x: this.mouseX,
        y: this.mouseY,
      });

      if (state.hoveredElement) {
        const rect = state.hoveredElement.getBoundingClientRect();
        highlightHtml = renderHighlight(rect, settings.annotationColor);
      }
    }

    // Selection rectangle HTML
    let selectionHtml = '';
    if (state.isSelecting && state.selectionRect) {
      const normalized = normalizeRect(state.selectionRect);
      selectionHtml = renderSelectionRect(normalized, settings.annotationColor);

      for (const element of state.selectionPreviewElements) {
        const rect = element.getBoundingClientRect();
        selectionHtml += renderHighlight(rect, settings.annotationColor);
      }
    }

    return html`
      <div
        class="annotation-root"
        @click=${this.handleClick}
        @change=${this.handleClick}
        @pointerdown=${this.handleToolbarPointerDown}
        @mouseover=${this.handleMouseOver}
        @mouseout=${this.handleMouseOut}
      >
        ${unsafeHTML(toolbarHtml)}
        ${unsafeHTML(annotationsPanelHtml)}
        ${unsafeHTML(markersHtml)}
        ${this.renderPopupTemplate(state)}
        ${unsafeHTML(tooltipHtml)}
        ${unsafeHTML(highlightHtml)}
        ${unsafeHTML(selectionHtml)}
      </div>
    `;
  }

  // Position toolbar after render
  protected firstUpdated() {
    this.positionToolbar();
  }

  protected willUpdate() {
    // Position toolbar on each update
    requestAnimationFrame(() => this.positionToolbar());
  }

  private popupResizeObserver: ResizeObserver | null = null;

  private syncPopupPosition() {
    if (!this.appState?.popupVisible) {
      this.popupPosition = null;
      this.teardownPopupObserver();
      return;
    }

    const popup = this.renderRoot?.querySelector('.popup-popover') as HTMLElement | null;
    if (!popup) return;

    const rect = popup.getBoundingClientRect();
    const clickX = this.appState.popupClickX;
    const clickY = this.appState.popupClickY;
    const nextPosition = calculatePopupPosition(clickX, clickY, { width: rect.width, height: rect.height });

    popup.style.left = `${nextPosition.left}px`;
    popup.style.top = `${nextPosition.top}px`;

    this.popupPosition = nextPosition;

    // Use ResizeObserver to re-position when popup dimensions change
    // (e.g. user types in textarea), instead of an unbounded polling timer.
    if (!this.popupResizeObserver) {
      this.popupResizeObserver = new ResizeObserver(() => {
        if (this.appState?.popupVisible) {
          this.syncPopupPosition();
        }
      });
      this.popupResizeObserver.observe(popup);
    }
  }

  private teardownPopupObserver() {
    if (this.popupResizeObserver) {
      this.popupResizeObserver.disconnect();
      this.popupResizeObserver = null;
    }
  }

  private handleToolbarPointerDown(event: PointerEvent) {
    if (!this.core || !this.appState) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (this.toolbarDrag) return;

    const target = event.target as HTMLElement | null;
    if (!target) return;
    if (target.closest('[data-annotation-settings], [data-annotation-list-panel]')) return;
    if (target.closest('select, textarea, input')) return;

    const toolbar = target.closest('[data-annotation-toolbar]') as HTMLElement | null;
    if (!toolbar) return;

    const rect = toolbar.getBoundingClientRect();
    this.toolbarDrag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: rect.left,
      originY: rect.top,
      width: rect.width,
      height: rect.height,
      didDrag: false,
    };

    document.addEventListener('pointermove', this.boundHandleToolbarPointerMove);
    document.addEventListener('pointerup', this.boundHandleToolbarPointerUp);
    document.addEventListener('pointercancel', this.boundHandleToolbarPointerUp);
  }

  private handleToolbarPointerMove(event: PointerEvent) {
    if (!this.toolbarDrag || event.pointerId !== this.toolbarDrag.pointerId) return;

    const toolbar = this.renderRoot.querySelector('.toolbar') as HTMLElement | null;
    if (!toolbar) return;

    if (!this.toolbarDrag.didDrag) {
      if (!hasExceededDragThreshold(this.toolbarDrag.startX, this.toolbarDrag.startY, event.clientX, event.clientY)) {
        return;
      }
      this.toolbarDrag.didDrag = true;
      this.suppressToolbarClick = true;
      toolbar.classList.add('dragging');
      setDraggingMode(true);
    }

    event.preventDefault();

    const next = computeDraggedToolbarPosition(
      { x: this.toolbarDrag.originX, y: this.toolbarDrag.originY },
      { x: this.toolbarDrag.startX, y: this.toolbarDrag.startY },
      { x: event.clientX, y: event.clientY },
      { width: this.toolbarDrag.width, height: this.toolbarDrag.height },
      { width: window.innerWidth, height: window.innerHeight }
    );

    this.dragPosition = next;
    toolbar.style.left = `${next.x}px`;
    toolbar.style.top = `${next.y}px`;
  }

  private handleToolbarPointerUp(event: PointerEvent) {
    if (!this.toolbarDrag || event.pointerId !== this.toolbarDrag.pointerId) return;

    const didDrag = this.toolbarDrag.didDrag;
    const nextPosition = this.dragPosition;
    this.teardownToolbarDrag();

    if (!didDrag || !nextPosition || !this.core) return;

    this.core.store.setState({
      toolbarPosition: nextPosition,
      hasCustomToolbarPosition: true,
      isDraggingToolbar: false,
    });
    this.core.eventBus.emit('toolbar:drag', { position: nextPosition });
    saveToolbarPosition(nextPosition);

    // Click fires after pointerup when the pointer stays on the button.
    // Clear the suppress flag on the next tick if that click never arrives.
    window.setTimeout(() => {
      this.suppressToolbarClick = false;
    }, 0);
  }

  private teardownToolbarDrag() {
    const toolbar = this.renderRoot?.querySelector('.toolbar') as HTMLElement | null;
    if (toolbar) {
      toolbar.classList.remove('dragging');
    }
    setDraggingMode(false);
    document.removeEventListener('pointermove', this.boundHandleToolbarPointerMove);
    document.removeEventListener('pointerup', this.boundHandleToolbarPointerUp);
    document.removeEventListener('pointercancel', this.boundHandleToolbarPointerUp);
    this.toolbarDrag = null;
    this.dragPosition = null;
  }

  private clampAndPersistToolbarPosition() {
    if (!this.core || !this.appState?.hasCustomToolbarPosition) return;

    const toolbar = this.renderRoot.querySelector('.toolbar') as HTMLElement | null;
    if (!toolbar) return;

    const clamped = clampToolbarPosition(
      this.appState.toolbarPosition.x,
      this.appState.toolbarPosition.y,
      { width: toolbar.offsetWidth, height: toolbar.offsetHeight },
      { width: window.innerWidth, height: window.innerHeight }
    );

    if (clamped.x === this.appState.toolbarPosition.x && clamped.y === this.appState.toolbarPosition.y) {
      return;
    }

    this.core.store.setState({ toolbarPosition: clamped });
    saveToolbarPosition(clamped);
  }

  private positionToolbar() {
    if (!this.appState) return;

    const toolbar = this.renderRoot.querySelector('.toolbar') as HTMLElement;
    if (!toolbar) return;

    if (this.toolbarDrag?.didDrag) {
      toolbar.classList.add('dragging');
    }

    const padding = TOOLBAR_VIEWPORT_PADDING;
    const toolbarSize = { width: toolbar.offsetWidth, height: toolbar.offsetHeight };
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    let x: number;
    let y: number;

    if (this.dragPosition) {
      x = this.dragPosition.x;
      y = this.dragPosition.y;
    } else if (this.appState.hasCustomToolbarPosition) {
      const clamped = clampToolbarPosition(
        this.appState.toolbarPosition.x,
        this.appState.toolbarPosition.y,
        toolbarSize,
        viewport,
        padding
      );
      x = clamped.x;
      y = clamped.y;
    } else {
      const { toolbarPosition } = this.appState.settings;

      switch (toolbarPosition) {
        case 'top-left':
          x = padding;
          y = padding;
          break;
        case 'top-right':
          x = window.innerWidth - toolbar.offsetWidth - padding;
          y = padding;
          break;
        case 'bottom-left':
          x = padding;
          y = window.innerHeight - toolbar.offsetHeight - padding;
          break;
        case 'bottom-right':
        default:
          x = window.innerWidth - toolbar.offsetWidth - padding;
          y = window.innerHeight - toolbar.offsetHeight - padding;
          break;
      }
    }

    toolbar.style.left = `${x}px`;
    toolbar.style.top = `${y}px`;
  }
}

/**
 * Register the custom element
 */
export function registerAnnotationElement(tagName: string = 'agent-ui-annotation') {
  if (!customElements.get(tagName)) {
    customElements.define(tagName, AnnotationElement);
  }
}
